/**
 * CONFRONTATION MÉCANIQUE AUX VALEURS DU TEXTE OFFICIEL.
 *
 * Conception du 23/08/2026, après l'échec de la réconciliation par modèle :
 * faire JUGER le modèle puis lui demander de « prouver » son jugement par une
 * citation littérale est une garantie illusoire — une citation exacte peut
 * accompagner une conclusion fausse (« trois ans » là où le texte dit « six
 * ans »).
 *
 * Ici, aucun jugement, aucun appel réseau, aucun appel de modèle : on extrait
 * mécaniquement les quantités juridiques (durées, pourcentages, seuils) du
 * TEXTE OFFICIEL et de la RÉPONSE, on les normalise, on les compare, et on
 * MONTRE les phrases du texte en vigueur. L'avocat tranche lui-même.
 *
 * Module PUR : directement testable, sans rendu ni serveur.
 */

/**
 * Longueur minimale d'un extrait officiel EXPLOITABLE (caractères). En deçà,
 * la valeur remontée est un fragment tronqué (« Art. L. 221-18 »), sans
 * contenu normatif à confronter : le plus court article du corpus d'éval
 * (art. 1128 C. civ., 62 caractères) reste très au-dessus de ce plancher.
 */
const MIN_OFFICIAL_TEXT_LEN = 25;

/** Nombre maximal d'extraits affichés. */
export const MAX_EXCERPTS = 5;
/** Longueur maximale d'un extrait affiché. */
export const MAX_EXCERPT_LENGTH = 300;

// ── Normalisation des nombres écrits en lettres ─────────────────────────────

const SIMPLE_WORDS: Record<string, number> = {
  un: 1, une: 1, deux: 2, trois: 3, quatre: 4, cinq: 5, six: 6, sept: 7,
  huit: 8, neuf: 9, dix: 10, onze: 11, douze: 12, treize: 13, quatorze: 14,
  quinze: 15, seize: 16, vingt: 20, vingts: 20, trente: 30, quarante: 40,
  cinquante: 50, soixante: 60, cent: 100, cents: 100,
};

/** Formes composées explicites, plus sûres qu'un analyseur général. */
const COMPOSED_WORDS: Record<string, number> = {
  "dix-sept": 17, "dix-huit": 18, "dix-neuf": 19,
  "soixante-dix": 70, "quatre-vingt": 80, "quatre-vingts": 80,
  "quatre-vingt-dix": 90,
};

function buildWordList(): string[] {
  const all = [...Object.keys(COMPOSED_WORDS), ...Object.keys(SIMPLE_WORDS)];
  // Composées d'abord, puis par longueur décroissante : « dix-sept » ne doit
  // jamais être lu comme « dix ».
  return all.sort((a, b) => b.length - a.length);
}

const WORD_ALT = buildWordList()
  .map((w) => w.replace(/[-]/g, "\\-"))
  .join("|");

/** Nombre en lettres (composé simple : « vingt-cinq », « soixante et onze »). */
const WORD_NUMBER_SRC = `(?:${WORD_ALT})(?:(?:[- ]et)?[- ](?:${WORD_ALT}))?`;
const DIGIT_NUMBER_SRC = String.raw`\d{1,3}(?:[  \u00A0]\d{3})*(?:[.,]\d+)?|\d+(?:[.,]\d+)?`;

function parseWordNumber(raw: string): number | undefined {
  const cleaned = raw.toLowerCase().replace(/\s+/g, "-").replace(/-et-/g, "-");
  if (COMPOSED_WORDS[cleaned] !== undefined) return COMPOSED_WORDS[cleaned];
  if (SIMPLE_WORDS[cleaned] !== undefined) return SIMPLE_WORDS[cleaned];
  const parts = cleaned.split("-");
  // Composition à deux termes : « vingt-cinq », « quatre-vingt-dix » (déjà
  // couvert), « soixante-douze ».
  if (parts.length >= 2) {
    const head = parts.slice(0, -1).join("-");
    const tail = parts[parts.length - 1];
    const h = COMPOSED_WORDS[head] ?? SIMPLE_WORDS[head];
    const t = SIMPLE_WORDS[tail];
    if (h !== undefined && t !== undefined && h % 10 === 0 && t < 20) return h + t;
  }
  return undefined;
}

function parseNumber(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (/^[\d]/.test(trimmed)) {
    const n = Number(trimmed.replace(/[  \u00A0]/g, "").replace(",", "."));
    return Number.isFinite(n) ? n : undefined;
  }
  return parseWordNumber(trimmed);
}

// ── Unités de quantité juridique ────────────────────────────────────────────

// QA 30/08/2026 — BUG 4 : les MONTANTS (amendes, seuils) étaient absents des
// unités confrontées, d'où une amende inventée sous une pastille verte.
const UNIT_SRC = String.raw`ans?|années?|mois|semaines?|jours?|heures?|%|pour\s+cent|pourcent|euros?|€`;

/** Unité canonique servant à la comparaison. */
function canonicalUnit(raw: string): string {
  const u = raw.toLowerCase().replace(/\s+/g, " ").trim();
  if (u === "%" || u === "pour cent" || u === "pourcent") return "pourcent";
  if (u.startsWith("an") || u.startsWith("ann")) return "an";
  if (u.startsWith("mois")) return "mois";
  if (u.startsWith("semaine")) return "semaine";
  if (u.startsWith("jour")) return "jour";
  if (u.startsWith("heure")) return "heure";
  if (u === "€" || u.startsWith("euro")) return "euro";
  return u;
}

const UNIT_LABEL: Record<string, string> = {
  an: "ans", mois: "mois", semaine: "semaines", jour: "jours",
  heure: "heures", pourcent: "%", euro: "euros",
};

const QUANTITY_RE = new RegExp(
  String.raw`(?<![\w-])(${DIGIT_NUMBER_SRC}|${WORD_NUMBER_SRC})\s*(${UNIT_SRC})(?![\w-])`,
  "gi",
);

export type Quantity = {
  /** Clé de comparaison normalisée, p. ex. « 6:an ». */
  key: string;
  /** Valeur numérique normalisée. */
  value: number;
  /** Unité canonique. */
  unit: string;
  /** Texte exactement tel qu'il figure dans la source. */
  raw: string;
  /** Libellé lisible normalisé, p. ex. « 6 ans ». */
  label: string;
};

/**
 * ARTICLE INDÉFINI vs NUMÉRAL — QA 18/09/2026.
 *
 * En français, « un » / « une » est le plus souvent un ARTICLE, pas un nombre.
 * « si l'échéance tombe un jour férié », « le lendemain est un jour ouvrable »
 * étaient lus comme la quantité « 1 jour », confrontés au texte officiel, et
 * signalés comme « valeur absente du texte en vigueur » — alors que la réponse
 * n'affirme aucune durée d'un jour. Une quantité « un/une » n'est donc retenue
 * que lorsqu'elle est introduite comme une DURÉE (« d'un an », « pendant une
 * heure », « sous un mois ») et qu'elle n'est pas suivie d'un qualificatif qui
 * en fait un nom commun (« un jour férié », « un jour ouvré »).
 */
const ONE_WORD_RE = /^une?$/i;
const ONE_INTRODUCER_RE =
  /(?:^|[\s(«"'’])(?:de|du|dans|pendant|durant|depuis|sous|apr[èe]s|avant|pour|par|[àa]|jusqu['’][àa]|d)['’\s]\s*$/i;

const ONE_QUALIFIER_RE =
  /^\s*(?:f[ée]ri[ée]s?|ouvrables?|ouvr[ée]s?|ch[ôo]m[ée]s?|calendaires?|francs?|de\s+(?:repos|cong[ée]|semaine|carence)|sur\s+deux|de\s+la\s+semaine)\b/i;

function isAmbiguousIndefinite(text: string, m: RegExpMatchArray): boolean {
  if (!ONE_WORD_RE.test(m[1].trim())) return false;
  const index = m.index ?? 0;
  const before = text.slice(0, index);
  // Fragment isolé (« un an » seul) : aucun contexte, on ne peut pas trancher
  // contre lui — la quantité est conservée.
  if (before.trim().length === 0) return false;
  const after = text.slice(index + m[0].length);
  if (ONE_QUALIFIER_RE.test(after)) return true;
  return !ONE_INTRODUCER_RE.test(before);
}

/** Extrait toutes les quantités juridiques d'un texte, sans les dédoublonner. */
export function extractQuantities(text: string): Quantity[] {
  const out: Quantity[] = [];
  if (!text) return out;
  for (const m of text.matchAll(QUANTITY_RE)) {
    const value = parseNumber(m[1]);
    if (value === undefined) continue;
    if (isAmbiguousIndefinite(text, m)) continue;
    const unit = canonicalUnit(m[2]);
    out.push({
      key: `${value}:${unit}`,
      value,
      unit,
      raw: m[0].trim(),
      label: `${value} ${UNIT_LABEL[unit] ?? unit}`.replace(" %", " %"),
    });
  }
  return out;
}


// ── Découpage en phrases ────────────────────────────────────────────────────

/**
 * Découpe le texte officiel en phrases. Les alinéas de code sont ponctués de
 * points-virgules et de sauts de ligne : on les traite comme des fins de
 * phrase, faute de quoi un alinéa entier de 3 000 caractères serait illisible.
 */
export function splitSentences(text: string): string[] {
  return text
    .split(/(?<=[.;:!?])\s+|\n+/)
    .map((s) => s.replace(/\s+/g, " ").trim())
    .filter((s) => s.length > 0);
}

/**
 * QA 09/09/2026 — BUG 3. Un extrait trop long était coupé à l'octet près, en
 * plein milieu d'un mot. On coupe désormais à la dernière frontière de
 * proposition (ponctuation ou, à défaut, espace) avant la limite.
 */
function truncate(sentence: string): string {
  if (sentence.length <= MAX_EXCERPT_LENGTH) return sentence;
  const window = sentence.slice(0, MAX_EXCERPT_LENGTH - 5);
  const punct = Math.max(
    window.lastIndexOf(", "),
    window.lastIndexOf("; "),
    window.lastIndexOf(" : "),
  );
  const cut = punct > MAX_EXCERPT_LENGTH / 2 ? punct : window.lastIndexOf(" ");
  const kept = (cut > 0 ? window.slice(0, cut) : window).trimEnd().replace(/[,;:]$/, "");
  return `${kept} […]`;
}

// ── Confrontation ───────────────────────────────────────────────────────────

export type OfficialExcerpt = {
  /** Phrase du texte officiel, RECOPIÉE telle quelle (jamais reformulée). */
  sentence: string;
  /** Quantités portées par cette phrase. */
  quantities: Quantity[];
};

export type ValueDiscrepancy = {
  /** Valeur affirmée par la réponse, telle qu'elle y figure. */
  raw: string;
  /** Libellé normalisé (« 3 ans »). */
  label: string;
};

export type OfficialValuePanel = {
  articleLabel: string;
  /** Version officielle du texte, ISO `AAAA-MM-JJ`. */
  versionDate: string;
  officialUrl?: string;
  excerpts: OfficialExcerpt[];
  /** Valeurs de la réponse introuvables dans le texte officiel. */
  discrepancies: ValueDiscrepancy[];
  /**
   * La réponse affirme au moins une valeur présente dans ce texte. Sinon les
   * phrases chiffrées de l'article ne se rapportent pas au propos (QA
   * 02/10/2026 : II de L. 442-1 affiché pour un raisonnement sur le I, 2°).
   */
  engaged?: boolean;
};

export type OfficialArticleInput = {
  label: string;
  versionDate: string;
  text?: string;
  officialUrl?: string;
};

/**
 * Confronte une réponse au texte officiel d'un article.
 *
 * EN CAS DE DOUTE, ON N'AFFICHE RIEN : texte absent, aucune quantité trouvée
 * dans le texte officiel ⇒ `undefined`, l'encart ne s'affiche pas et le
 * drapeau de récence subsiste seul.
 */
/**
 * Termes lexicaux significatifs d'une phrase : mots d'au moins six lettres,
 * sans accent, réduits à leur préfixe de six caractères (« conservation » et
 * « conserver » se rejoignent), amputés du vocabulaire passe-partout du droit
 * qui rapprocherait n'importe quelles phrases entre elles.
 */
const GENERIC_TERMS = new Set(
  [
    "article",
    "alinea",
    "alineas",
    "societe",
    "societes",
    "transmission",
    "transmise",
    "transmises",
    "conditions",
    "present",
    "presente",
    "mentionne",
    "mentionnees",
    "premier",
    "second",
    "valeur",
    "valeurs",
    "personne",
    "delai",
    "delais",
    "duree",
    "durees",
    "pendant",
  ].map((w) => w.slice(0, 6)),
);

export function significantTerms(sentence: string): Set<string> {
  const plain = sentence
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  const out = new Set<string>();
  for (const word of plain.match(/[a-z]{6,}/g) ?? []) {
    const stem = word.slice(0, 6);
    if (!GENERIC_TERMS.has(stem)) out.add(stem);
  }
  return out;
}

/**
 * COUPLES D'OPPOSITION du vocabulaire juridique. Deux régimes voisins se
 * distinguent souvent par un seul qualificatif : l'article 787 B du Code
 * général des impôts oppose l'engagement COLLECTIF (deux ans, trois années)
 * à l'engagement INDIVIDUEL (six ans). Quand la réponse retient un côté et
 * le voisinage officiel l'autre, ce voisinage n'est PAS celui dont parle la
 * réponse : il est écarté au lieu d'être compté.
 */
const CONTRAST_PAIRS: readonly (readonly [string, string])[] = [
  ["individuel", "collectif"],
  ["minimale", "maximale"],
  ["donation", "succession"],
];

const CONTRASTS = CONTRAST_PAIRS.flatMap(([a, b]) => [
  [a.slice(0, 6), b.slice(0, 6)] as const,
  [b.slice(0, 6), a.slice(0, 6)] as const,
]);

function countShared(a: Set<string>, b: Set<string>): number {
  let n = 0;
  for (const t of a) if (b.has(t)) n += 1;
  for (const [side, opposite] of CONTRASTS) {
    if (a.has(side) && b.has(opposite) && !b.has(side)) n -= 2;
  }
  return n;
}

/**
 * Voisinage immédiat d'une valeur dans une phrase : ce qui la précède et la
 * suit directement, seul contexte pertinent pour savoir DE QUOI la valeur
 * parle. Repli sur la phrase entière si la valeur n'y est pas retrouvée.
 */
export function neighbourhood(sentence: string, raw: string, before = 130, after = 90): string {
  const idx = sentence.toLowerCase().indexOf(raw.toLowerCase());
  if (idx < 0) return sentence;
  return sentence.slice(Math.max(0, idx - before), idx + raw.length + after);
}

/**
 * FAMILLE D'UNITÉS — QA 30/08/2026 (BUG 4), élargie le 08/09/2026.
 *
 * Une même durée s'exprime indifféremment en heures, jours, semaines, mois ou
 * années : « vingt-quatre heures » et « un jour » désignent le même délai,
 * « douze mois » et « un an » également. Toutes les unités de temps sont donc
 * ramenées à une base commune (l'heure) avant comparaison, faute de quoi une
 * simple reformulation du texte officiel était comptée comme un écart.
 */
const HOURS_PER_UNIT: Record<string, number> = {
  heure: 1,
  jour: 24,
  semaine: 24 * 7,
  mois: 24 * 30,
  an: 24 * 360,
};

function familyOf(unit: string): string {
  return HOURS_PER_UNIT[unit] !== undefined ? "duree" : unit;
}

function familyKey(q: Quantity): string {
  const factor = HOURS_PER_UNIT[q.unit];
  return factor === undefined ? q.key : `${q.value * factor}:duree`;
}

/**
 * PRÉMISSE ERRONÉE REPRISE POUR ÊTRE RÉFUTÉE.
 *
 * Une bonne réponse commence souvent par rappeler le chiffre inexact avancé
 * par l'utilisateur (« vous indiquez cinquante ans ») avant de le corriger.
 * Cette valeur n'est pas affirmée : la compter comme un écart pénalisait
 * précisément les réponses les plus rigoureuses. Les phrases porteuses d'une
 * marque de réfutation explicite sont donc exclues de la confrontation.
 */
const REFUTATION_RE = new RegExp(
  [
    String.raw`\bn['’ ]?(?:est|sont|e|a|ont|aura|auront|était|étaient)\b[^.;:!?]{0,60}\b(?:pas|plus|jamais|nullement|point)\b`,
    // QA 09/09/2026, BUG 2.a — « Je ne confirme donc pas les sept jours » :
    // l'adverbe intercalé entre le verbe et la négation faisait échouer le
    // motif, et la réponse la plus rigoureuse était pénalisée. On tolère
    // jusqu'à trois mots entre le verbe et « pas / plus / jamais ».
    String.raw`\bne\s+(?:se\s+)?\w+(?:ent|e|ons|ez|ait|aient|ais|is|us)\s+(?:\w+['’]?\s+){0,3}(?:pas|plus|jamais|nullement|point)\b`,
    String.raw`\bne\s+(?:saurai(?:t|ent|s)|peut|peuvent|puis)\b[^.;:!?]{0,40}\b(?:pas|plus|jamais|être\s+confirm)`,
    String.raw`\bnon\s+plus\s+de\b`,
    String.raw`\b(?:inexact|inexacte|erron[ée]e?s?|fausse?s?|faux|erreur|contrairement|à\s+tort|id[ée]e\s+re[çc]ue|prétendument|prescription\s+erron)`,
    String.raw`\b(?:et\s+non|non\s+pas|au\s+lieu\s+de|alors\s+qu[e’'])\b`,
    // Reprise explicite de la prémisse de l'utilisateur avant correction.
    String.raw`\b(?:vous\s+(?:indiquez|mentionnez|évoquez|avancez|pensez|croyez|aviez\s+\w+)|(?:vous|tu)\s+m['’]a(?:vez|s)\s+dit|(?:je\s+)?ne\s+confirme|on\s+(?:vous|m['’])a\s+dit|on\s+(?:vous|m['’])?\s*dit\b|l['’]affirmation|votre\s+pr[ée]misse|la\s+pr[ée]misse|votre\s+question\s+repose|ces\s+(?:sept|huit|dix|quatorze|trente)\b)`,

    // QA 09/09/2026 (2e passe) — ÉLISION. « ne s'applique plus », « ne l'est
    // pas » : l'apostrophe collée au pronom faisait échouer le motif « ne se ».
    String.raw`\bne\s+[a-zà-ÿ]{1,3}['’]\w+\s+(?:\w+['’]?\s+){0,3}(?:pas|plus|jamais|nullement|point)\b`,
    // RÉFUTATION PAR SUBSTITUTION : « la règle est celle des quatorze jours,
    // non celle de sept jours ».
    String.raw`\bnon\s+(?:celle|celui|ceux|celles|ce|cet|cette|ces|le|la|les|un|une|de|d['’])\b`,
    String.raw`\b(?:en\s+r[ée]alit[ée]|en\s+v[ée]rit[ée]|or,)\b`,
    // Ancien état du droit rappelé pour être écarté (« avant la loi de 2017,
    // le délai était de trois ans »).
    String.raw`\b(?:auparavant|antérieurement|avant\s+la\s+(?:loi|réforme)|ancienne\s+r[ée]daction|jusqu['’]en\s+\d{4}|d[ée]sormais|depuis\s+la\s+(?:loi|réforme))\b`,
    // OBSOLESCENCE : la valeur est rappelée comme n'étant plus le droit positif.
    String.raw`\b(?:ancien(?:ne)?s?\s+(?:r[ée]gime|droit|r[ée]daction|d[ée]lai|version|texte|[ée]tat\s+du\s+droit)|n['’]est\s+plus\s+applicable|ne\s+s['’]applique\s+plus|a\s+[ée]t[ée]\s+(?:remplac|abrog|supprim|modifi)|abrog[ée]e?s?|obsol[èe]te|p[ée]rim[ée]e?)\b`,
  ].join("|"),
  "i",
);

/** VRAI si la phrase cite une valeur pour la réfuter plutôt que l'affirmer. */
export function isRefutationSentence(sentence: string): boolean {
  return REFUTATION_RE.test(sentence);
}


/**
 * VALEURS DU CAS D'ESPÈCE — jamais confrontées au texte officiel.
 *
 * « Des faits vieux de cinq ans », « vous avez signé il y a deux mois » : ces
 * quantités décrivent la situation racontée par l'utilisateur, pas une règle
 * de droit. Les confronter à Légifrance n'a aucun sens et produisait des
 * signalements systématiques sur des réponses parfaitement exactes.
 */
const CASE_FACT_RE = new RegExp(
  [
    String.raw`\b(?:les\s+)?faits\b`,
    String.raw`\bremonte(?:nt|raient)?\s+à\b`,
    String.raw`\bvieux\s+de\b`,
    String.raw`\bdatent?\s+de\b`,
    String.raw`\bil\s+y\s+a\s+(?:environ\s+|près\s+de\s+|plus\s+de\s+)?(?:\d|un|une|deux|trois|quatre|cinq|six|sept|huit|neuf|dix)`,
    String.raw`\bdans\s+votre\s+(?:cas|situation|espèce|hypothèse)\b`,
    String.raw`\bvotre\s+(?:situation|cas|contrat|licenciement|plainte)\b`,
    // QA 22/09/2026 — les données personnelles énoncées par l'utilisateur
    // (durée de SA période d'essai, SON ancienneté, SA présence effective)
    // décrivent le cas d'espèce : « votre période d'essai de deux mois » était
    // confrontée au texte officiel, qui ne porte évidemment pas cette valeur.
    String.raw`\bvotre\s+(?:p[ée]riode\s+d['’]essai|anciennet[ée]|pr[ée]sence|embauche|essai)\b`,
    String.raw`\bcela\s+fait\b`,
    String.raw`\bvous\s+(?:avez|indiquez|précisez|évoquez|subissez|travaillez|êtes|comptez|totalisez)\b`,
  ].join("|"),
  "i",
);

/** VRAI si la phrase décrit le cas d'espèce plutôt qu'une règle de droit. */
export function isCaseFactSentence(sentence: string): boolean {
  return CASE_FACT_RE.test(sentence);
}

/**
 * RÉSERVE GÉNÉRALE / EXEMPLE — QA 10/09/2026, BUG 2.
 *
 * « Certains contentieux connaissent en effet des délais particuliers,
 * notamment de quinze jours ou de dix jours. » Cette phrase n'attribue AUCUNE
 * valeur à l'article vérifié : elle met en garde contre l'existence d'autres
 * régimes. La confronter au texte de l'article 538 du Code de procédure civile
 * faisait chuter à 40 % le badge d'une réponse pourtant intégralement exacte,
 * dont les sept références étaient confirmées et en vigueur.
 *
 * Les valeurs figurant dans une formulation NON ASSERTIVE — énumération
 * exemplative, hypothèse, réserve, renvoi à d'autres régimes — sont donc
 * écartées de la confrontation, exactement comme les prémisses réfutées et les
 * chiffres du cas d'espèce.
 */
const HEDGED_RE = new RegExp(
  [
    String.raw`\bnotamment\b`,
    String.raw`\b(?:par|à titre d['’])\s*exemple\b`,
    String.raw`\bcertain(?:s|es)\s+(?:contentieux|procédures?|matières?|cas|recours|décisions?|litiges?|domaines?)\b`,
    String.raw`\bdans\s+certains\s+cas\b`,
    String.raw`\bd['’]autres\s+(?:délais|régimes|textes|règles)\b`,
    String.raw`\bdélais?\s+(?:particuliers?|spécifiques?|dérogatoires?|abrégés?|spéciaux)\b`,
    String.raw`\ble\s+cas\s+[ée]ch[ée]ant\b`,
    String.raw`\bselon\s+(?:les\s+cas|la\s+nature|la\s+matière|le\s+type)\b`,
    String.raw`\b(?:il\s+(?:convient|faut|y\s+a\s+lieu)\s+de\s+v[ée]rifier|v[ée]rifiez)\b`,
    String.raw`\bsauf\s+(?:disposition|texte|r[èe]gle|d[ée]lai|si)\b`,
    String.raw`\b(?:peut|peuvent|pourrait|pourraient)\s+(?:être\s+)?(?:varier|ramen[ée]|r[ée]duit|port[ée]|abr[ée]g[ée])`,
  ].join("|"),
  "i",
);

/**
 * VRAI si la phrase évoque une valeur à titre d'exemple ou de réserve, sans
 * l'attribuer à l'article confronté.
 */
export function isHedgedSentence(sentence: string): boolean {
  return HEDGED_RE.test(sentence);
}



/**
 * VRAI si la chaîne commence une phrase / un alinéa complet : majuscule,
 * guillemet ouvrant, numérotation d'alinéa (« 1° », « I. ») ou mention
 * « Article ». Un fragment tel que « 1242-3 et, en outre, … » est rejeté.
 */
export function isSentenceStart(sentence: string): boolean {
  const s = (sentence || "").trim();
  if (!s) return false;
  if (/^\d+°/.test(s)) return true;                    // 1° alinéa numéroté
  if (/^[IVX]+\.?\s/.test(s)) return true;             // I. II. III.
  if (/^(?:art\.?|article)\b/i.test(s)) return true;
  if (/^[«"(]/.test(s)) return true;
  // Fragment de numéro d'article (« 1242-3 et … ») : rejeté.
  if (/^\d+[-–.]\d/.test(s)) return false;
  return /^[A-ZÀ-ÝÆŒ]/.test(s);
}

/** Retire les fragments de tête issus d'un texte officiel tronqué. */
export function dropLeadingFragment(sentences: readonly string[]): string[] {
  const out = [...sentences];
  while (out.length > 0 && !isSentenceStart(out[0])) out.shift();
  return out;
}

/**
 * Le texte officiel doit appartenir à l'article sous lequel il est affiché :
 * s'il s'ouvre sur un intitulé « Article X » dont le numéro diffère de celui
 * de l'encart, l'attribution est fausse et l'encart n'est pas construit.
 */
export function officialTextMatchesArticle(text: string, articleLabel: string): boolean {
  const head = (text || "").slice(0, 120);
  const inText = /\b(?:article|art\.)\s*((?:[LRDA]\.?\s?)?\d{1,4}(?:[-–.]\d{1,4})*)/i.exec(head);
  if (!inText) return true;
  const inLabel = /((?:[LRDA]\.?\s?)?\d{1,4}(?:[-–.]\d{1,4})*)/.exec(articleLabel || "");
  if (!inLabel) return true;
  const norm = (v: string) => v.toLowerCase().replace(/[^0-9a-z]/g, "");
  return norm(inText[1]) === norm(inLabel[1]);
}

/** Numéro d'article normalisé (« L. 1245-1 » → « l12451 »). */
function normArticleNumber(value: string): string {
  return value.toLowerCase().replace(/[^0-9a-z]/g, "");
}

/** Recouvrement lexical minimal pour rattacher une valeur à un article. */
const MIN_TOPIC_OVERLAP = 1;

/**
 * Noms de matière trop larges pour rattacher une valeur à un article : presque
 * tout article du Code du travail parle du « contrat » ou du « salarié », tout
 * article du divorce de la « convention ». Partager ce seul mot ne prouve pas
 * que la valeur et l'article traitent du même point.
 */
const TOPIC_GENERIC = new Set(["contra", "conven", "employ", "salari"]);

/**
 * Article auquel une phrase de la réponse rattache expressément son propos :
 * « self » si elle cite l'article de l'encart, « other » si elle ne cite que
 * d'autres articles, « none » si elle n'en cite aucun.
 */
export function sentenceArticleAnchor(
  sentence: string,
  articleLabel: string,
): "self" | "other" | "none" {
  const label = /((?:[LRDA]\.?\s?)?\d{1,4}(?:[-–.]\d{1,4})*)/.exec(articleLabel || "");
  if (!label) return "none";
  const target = normArticleNumber(label[1]);
  const re = /\b(?:articles?|art\.)\s*((?:[LRDA]\.?\s?)?\d{1,4}(?:[-–.]\d{1,4})*)/gi;
  let cited = false;
  for (const m of sentence.matchAll(re)) {
    cited = true;
    if (normArticleNumber(m[1]) === target) return "self";
  }
  return cited ? "other" : "none";
}

/**
 * QA 09/09/2026 — BUG 3, chemin réel de production. Le texte officiel rapatrié
 * n'est pas toujours celui d'un SEUL article : Légifrance renvoie fréquemment
 * un bloc contenant l'article demandé PUIS le suivant (« … Article L. 1245-2 -
 * Elle ouvre droit à une indemnité … »). L'ancien contrôle ne regardait que
 * les 120 premiers caractères : l'intitulé enchâssé passait inaperçu et le
 * texte de l'article suivant était affiché sous l'article précédent.
 *
 * On découpe donc le texte à chaque intitulé d'article situé en tête de ligne
 * ou après une fin de phrase, et l'on ne conserve QUE les segments portant le
 * numéro de l'encart. Aucun segment attribuable ⇒ aucun encart.
 */
export function sliceOfficialTextForArticle(
  text: string,
  articleLabel: string,
): string | undefined {
  const raw = (text || "").trim();
  if (!raw) return undefined;
  const inLabel = /((?:[LRDA]\.?\s?)?\d{1,4}(?:[-–.]\d{1,4})*)/.exec(articleLabel || "");
  if (!inLabel) return raw;
  const target = normArticleNumber(inLabel[1]);

  /**
   * RE-TEST 09/09/2026 — le découpage ne doit JAMAIS amputer un article qui
   * renvoie en plein texte à d'autres articles (« … des articles L. 1242-1 à
   * L. 1242-4 … », « en application de l'article L. 1243-13 »). Un intitulé
   * n'est donc tenu pour une rupture que s'il est suivi d'un SÉPARATEUR
   * d'intitulé (tiret, deux-points, saut de ligne) et jamais lorsqu'il est
   * introduit par « de / à / aux / des / en application de l'article ».
   */
  const headerRe =
    /(?:^|\n|(?<=[.;!?]["»)\]]?\s))[ \t]*(?:article|art\.)\s*((?:[LRDA]\.?\s?)?\d{1,4}(?:[-–.]\d{1,4})*)\s*(?:[-–—:]\s|\r?\n|$)/gi;
  const INTRODUCED_RE = /(?:\b(?:de|d[eu]s|à|aux|dans|selon|vis[ée]s? (?:à|aux)|application)\s+(?:l['’]|les\s+)?)$/i;
  const headers: { index: number; number: string }[] = [];
  let m: RegExpExecArray | null;
  while ((m = headerRe.exec(raw)) !== null) {
    const before = raw.slice(Math.max(0, m.index - 40), m.index + (m[0].length - m[0].trimStart().length));
    if (INTRODUCED_RE.test(before.replace(/\s+$/, " "))) continue;
    headers.push({ index: m.index, number: normArticleNumber(m[1]) });
  }
  if (headers.length === 0) return raw;

  const owned: string[] = [];
  headers.forEach((h, i) => {
    if (h.number !== target) return;
    const end = headers[i + 1]?.index ?? raw.length;
    owned.push(raw.slice(h.index, end).trim());
  });
  // Le texte peut commencer par l'article visé SANS intitulé, l'intitulé
  // suivant appartenant alors à l'article voisin : ce préambule est conservé.
  if (headers[0].index > 0 && headers[0].number !== target) {
    owned.unshift(raw.slice(0, headers[0].index).trim());
  }
  const joined = owned.filter(Boolean).join("\n").trim();
  return joined.length > 0 ? joined : undefined;
}

export function buildOfficialValuePanel(
  answer: string,
  article: OfficialArticleInput,
): OfficialValuePanel | undefined {
  const rawText = (article.text ?? "").trim();
  if (rawText.length < MIN_OFFICIAL_TEXT_LEN) return undefined;

  // QA 09/09/2026 — BUG 3. Le texte officiel rapatrié peut commencer AU MILIEU
  // d'une phrase (« 1242-3 et, en outre, lorsque l'apprenti… »), porter en
  // tête un numéro d'article DIFFÉRENT de celui sous lequel l'encart s'affiche,
  // ou contenir à la suite le texte de l'article voisin. Dans tous les cas on
  // n'affiche rien plutôt qu'un extrait mal attribué ou coupé en plein mot.
  const text = sliceOfficialTextForArticle(rawText, article.label);
  if (!text || text.length < MIN_OFFICIAL_TEXT_LEN) return undefined;
  if (!officialTextMatchesArticle(text, article.label)) return undefined;

  const sentences = dropLeadingFragment(splitSentences(text)).filter(
    // Fragment de numéro d'article laissé par une troncature amont.
    (s) => !/^\d+[-–.]\d/.test(s),
  );
  if (sentences.length === 0) return undefined;

  const officialQuantities: Quantity[] = [];
  const excerpts: OfficialExcerpt[] = [];
  /** Phrases officielles non tronquées, alignées sur `excerpts`. */
  const fullSentences: string[] = [];
  for (const sentence of sentences) {
    const quantities = extractQuantities(sentence);
    if (quantities.length === 0) continue;
    officialQuantities.push(...quantities);
    excerpts.push({ sentence: truncate(sentence), quantities });
    fullSentences.push(sentence);
  }
  if (officialQuantities.length === 0) return undefined;

  const officialUnits = new Set(officialQuantities.map((q) => q.unit));
  const officialKeys = new Set(officialQuantities.map((q) => q.key));
  // Comparaison de repli PAR FAMILLE : « six mois » face à « un an ».
  
  const officialFamilyKeys = new Set(officialQuantities.map(familyKey));

  /**
   * RATTACHEMENT CONTEXTUEL — purement lexical, aucun jugement de modèle.
   *
   * Un article long porte plusieurs durées sans rapport entre elles : l'article
   * 787 B du Code général des impôts mentionne « trois ans » pour la détention
   * d'actifs, « deux ans » pour l'engagement collectif et « six ans » pour
   * l'engagement individuel. Se contenter de chercher la valeur QUELQUE PART
   * dans le texte ne prouve donc rien. On compare le VOISINAGE IMMÉDIAT de la
   * valeur affirmée par la réponse au voisinage immédiat de chaque valeur du
   * texte officiel ; la valeur n'est tenue pour retrouvée que si le voisinage
   * le plus proche porte bien cette valeur.
   */
  const officialContexts = excerpts.flatMap((e, i) =>
    e.quantities.map((q) => ({
      excerptIndex: i,
      key: q.key,
      unit: q.unit,
      terms: significantTerms(neighbourhood(fullSentences[i] ?? e.sentence, q.raw)),
    })),
  );

  const discrepancies: ValueDiscrepancy[] = [];
  const seen = new Set<string>();
  const excerptScore = new Map<number, number>();
  /** La réponse affirme-t-elle une valeur présente dans ce texte officiel ? */
  let engaged = false;

  /**
   * FILET DE SÉCURITÉ — QA 09/09/2026 (2e passe).
   *
   * Une réponse qui CORRIGE l'utilisateur répète nécessairement le mauvais
   * chiffre. Si ce chiffre n'apparaît QUE dans des phrases de réfutation ou
   * d'obsolescence, il n'est jamais affirmé : il ne doit ni être signalé, ni
   * faire chuter le score. On relève donc d'abord toutes les valeurs portées
   * par ces phrases, pour les retirer ensuite des écarts.
   */
  const refutedKeys = new Set<string>();
  for (const s of splitSentences(answer)) {
    if (!isRefutationSentence(s)) continue;
    for (const q of extractQuantities(s)) {
      refutedKeys.add(q.key);
      refutedKeys.add(familyKey(q));
    }
  }

  for (const answerSentence of splitSentences(answer)) {
    // Prémisse erronée reprise pour être corrigée : rien n'est affirmé ici.
    if (isRefutationSentence(answerSentence)) continue;
    // Chiffres du cas d'espèce (âge des faits, dates fournies) : hors sujet.
    if (isCaseFactSentence(answerSentence)) continue;
    // Réserve générale ou exemple (« notamment de dix jours ») : aucune valeur
    // n'est attribuée à l'article confronté. QA 10/09/2026, BUG 2.
    if (isHedgedSentence(answerSentence)) continue;



    const anchor = sentenceArticleAnchor(answerSentence, article.label);
    // La phrase rattache expressément son propos à un AUTRE article : ses
    // valeurs ne relèvent pas de cet encart.
    if (anchor === "other") continue;

    for (const q of extractQuantities(answerSentence)) {
      // Sans point de comparaison dans le texte officiel, on ne signale rien.
      if (seen.has(q.key)) continue;
      // Valeur par ailleurs explicitement réfutée / donnée pour obsolète.
      if (refutedKeys.has(q.key) || refutedKeys.has(familyKey(q))) continue;

      const sameValueElsewhere = officialFamilyKeys.has(familyKey(q));

      /**
       * QA 28/09/2026 — RATTACHEMENT OBLIGATOIRE AVANT TOUTE ACCUSATION.
       *
       * Une réponse citant quatre articles était confrontée, valeur par
       * valeur, à CHAQUE article pris isolément : « trente-six mois » (CDD à
       * objet défini) était déclarée absente de l'article L. 1245-2, qui ne
       * traite que de la requalification ; « sept jours » (dépôt chez le
       * notaire, art. 229-1 C. civ.) était déclarée absente de l'article
       * portant le délai de réflexion de quinze jours. L'article confronté ne
       * PARLAIT PAS de ces valeurs : son silence ne prouve rien.
       *
       * Une valeur absente n'est donc signalée que si elle est rattachée à cet
       * article : soit la phrase le cite expressément, soit le voisinage de la
       * valeur partage au moins deux termes significatifs avec le voisinage
       * d'une valeur de même famille dans le texte officiel (même sujet).
       */
      if (!officialKeys.has(q.key) && !sameValueElsewhere && anchor !== "self") {
        const terms = new Set(
          [...significantTerms(neighbourhood(answerSentence, q.raw))].filter(
            (t) => !TOPIC_GENERIC.has(t),
          ),
        );
        const related = officialContexts
          .filter((c) => familyOf(c.unit) === familyOf(q.unit))
          .reduce((m, c) => Math.max(m, countShared(terms, c.terms)), 0);
        if (related < MIN_TOPIC_OVERLAP) continue;
      }

      // Unité absente du texte officiel. La conversion entre unités de temps
      // sert d'abord à DISCULPER une reformulation équivalente. Elle ne sert à
      // accuser qu'entre l'année et le mois, seules unités réellement
      // interchangeables dans la rédaction des textes (une peine de « six
      // mois » face à « un an »). Une durée en heures ou en jours face à un
      // texte rédigé en années parle presque toujours d'autre chose : silence.
      if (!officialUnits.has(q.unit)) {
        const interchangeable = q.unit === "an" || q.unit === "mois";
        const officialHasBoth = officialUnits.has("an") || officialUnits.has("mois");
        if (!interchangeable || !officialHasBoth) continue;
        if (!sameValueElsewhere) {
          seen.add(q.key);
          discrepancies.push({ raw: q.raw, label: q.label });
        }
        continue;
      }



      // La valeur n'existe nulle part dans le texte, même reformulée : écart.
      if (!officialKeys.has(q.key) && !sameValueElsewhere) {
        seen.add(q.key);
        discrepancies.push({ raw: q.raw, label: q.label });
        continue;
      }

      engaged = true;
      const terms = significantTerms(neighbourhood(answerSentence, q.raw));
      const sameUnit = officialContexts.filter((c) => c.unit === q.unit);
      const scored = sameUnit.map((c) => ({ c, score: countShared(terms, c.terms) }));
      for (const { c, score } of scored) {
        excerptScore.set(c.excerptIndex, Math.max(excerptScore.get(c.excerptIndex) ?? 0, score));
      }
      const best = scored.reduce((m, x) => Math.max(m, x.score), 0);
      // Aucun rapprochement lexical possible : contexte inexploitable, on se tait.
      if (best === 0) continue;

      const closest = new Set(scored.filter((x) => x.score === best).map((x) => x.c.key));
      // Plusieurs valeurs se disputent le même voisinage : ambiguïté, on se tait.
      if (closest.size > 1) continue;
      if (closest.has(q.key)) continue;

      // La valeur EXISTE bien dans le texte officiel, mais un autre voisinage
      // a mieux collé au propos. Si le voisinage officiel qui PORTE la valeur
      // affirmée partage lui aussi du vocabulaire avec la réponse, les deux
      // parlent plausiblement de la même chose : le classement ne prouve alors
      // rien et l'on se tait (art. 8 du Code de procédure pénale : « six
      // années révolues » face à une réponse qui dit « six ans »).
      const ownContexts = scored.filter((x) => x.c.key === q.key);
      const bestSameValue = ownContexts.reduce((m, x) => Math.max(m, x.score), 0);
      if (bestSameValue >= 1) continue;

      /**
       * QA 22/09/2026 — FAUX NÉGATIF SUR UN PALIER NUMÉROTÉ.
       *
       * L'article L. 1221-25 du Code du travail énumère ses paliers en
       * propositions télégraphiques : « 4° Un mois après trois mois de
       * présence. » Ce voisinage ne porte qu'un seul terme lexical utile
       * (« présence ») : dès que la réponse parlait d'ancienneté plutôt que de
       * présence, le rapprochement retombait à zéro, un autre palier du même
       * article (« Deux semaines après un mois de présence ») l'emportait au
       * classement, et « trois mois » était signalé « absente du texte en
       * vigueur » alors que l'extrait affiché juste au-dessus la contient
       * LITTÉRALEMENT. Le score de confiance chutait à 40 % sur une réponse
       * intégralement sourcée.
       *
       * Un voisinage aussi pauvre n'est pas un contexte exploitable : il ne
       * peut ni confirmer ni infirmer quoi que ce soit. Lorsque AUCUN des
       * voisinages officiels portant la valeur affirmée n'offre au moins deux
       * termes lexicaux, le classement ne prouve rien et l'on se tait.
       */
      const MIN_OWN_CONTEXT_TERMS = 2;
      const ownContextIsThin = ownContexts.every(
        (x) => x.c.terms.size < MIN_OWN_CONTEXT_TERMS,
      );
      if (ownContextIsThin) continue;





      seen.add(q.key);
      discrepancies.push({ raw: q.raw, label: q.label });
    }
  }


  // QA 02/10/2026 — seules les phrases portant une valeur chiffrée sont
  // candidates. Quand la réponse ne s'appuie sur AUCUNE d'elles (ex. L. 442-1,
  // I, 2° — déséquilibre significatif, sans durée), on affichait quand même la
  // seule phrase chiffrée de l'article (II, préavis de dix-huit mois) sous
  // l'intitulé « texte en vigueur » : un extrait hors sujet présenté comme
  // preuve. Si la réponse n'affirme AUCUNE valeur retrouvée dans ce texte et
  // qu'aucun écart n'est à signaler, aucun encart.
  const scored = excerpts
    .map((e, i) => ({ e, i, score: excerptScore.get(i) ?? 0 }))
    .sort((a, b) => b.score - a.score || a.i - b.i);
  const ordered = scored
    .slice(0, MAX_EXCERPTS)
    .map((x) => x.e);

  return {
    articleLabel: article.label,
    versionDate: article.versionDate,
    officialUrl: article.officialUrl,
    excerpts: ordered,
    discrepancies,
    engaged,
  };
}

/**
 * Construit les encarts pour toutes les références récemment modifiées dont le
 * texte officiel est disponible. Aucun appel réseau : les textes proviennent
 * déjà de la vérification des références.
 *
 * CORRECTION 08/09/2026 — une réponse cite plusieurs articles ; chaque encart
 * était construit ISOLÉMENT, si bien qu'une valeur confirmée par l'article 8 du
 * Code de procédure pénale était malgré tout signalée « absente » par l'encart
 * d'un autre article, qui n'en parle simplement pas. Une valeur retrouvée dans
 * UN SEUL des textes officiels est donc désormais retirée de tous les encarts.
 */
export function buildOfficialValuePanels(
  answer: string,
  articles: readonly OfficialArticleInput[] | undefined,
): OfficialValuePanel[] {
  const list = articles ?? [];
  const built = list.map((article) => ({
    article,
    panel: buildOfficialValuePanel(answer, article),
  }));

  const keysOf = (text: string | undefined) => {
    const set = new Set<string>();
    for (const q of extractQuantities(text ?? "")) {
      set.add(q.key);
      set.add(familyKey(q));
    }
    return set;
  };
  const perArticle = list.map((a) => keysOf(a.text));

  const out: OfficialValuePanel[] = [];
  built.forEach(({ panel }, index) => {
    if (!panel) return;
    // Valeurs portées par les AUTRES textes officiels cités. Le texte de
    // l'article courant est volontairement exclu : sa propre confrontation
    // contextuelle a déjà tranché (une valeur présente ailleurs dans le même
    // article, mais dans un autre contexte, reste un écart).
    const elsewhere = new Set<string>();
    perArticle.forEach((keys, i) => {
      if (i === index) return;
      for (const k of keys) elsewhere.add(k);
    });

    const discrepancies = panel.discrepancies.filter((d) => {
      const q = extractQuantities(d.raw)[0];
      if (!q) return true;
      return !elsewhere.has(q.key) && !elsewhere.has(familyKey(q));
    });
    // Aucune valeur de la réponse ne se rapporte à ce texte et aucun écart :
    // ses phrases chiffrées seraient hors sujet sous « texte en vigueur ».
    if (panel.engaged === false && discrepancies.length === 0) return;
    out.push({ ...panel, discrepancies });
  });

  return out;
}



/** VRAI si au moins un écart de valeur a été relevé. */
export function hasValueDiscrepancy(panels: readonly OfficialValuePanel[]): boolean {
  return panels.some((p) => p.discrepancies.length > 0);
}
